import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../stores/authStore'
import { queryKeys } from '../queryKeys'

// Type guard for Supabase storage errors
function isStorageApiError(error: unknown): error is { message: string; status: number } {
  return typeof error === 'object' && error !== null && 'status' in error && 'message' in error
}

// CSV upload info interface (derived from storage metadata)
export interface CSVUpload {
  fileName: string
  fileSize: number
  uploadDate: string
}

export function useUserCSVUpload() {
  const { user } = useAuth()

  return useQuery({
    queryKey: queryKeys.userCSVUpload(user?.id || ''),
    queryFn: async (): Promise<CSVUpload | null> => {
      if (!user) throw new Error('No authenticated user')

      // Check if user has a CSV file in storage
      const { data, error } = await supabase.storage.from('user-csv-files').list('', {
        limit: 1,
        search: user.id
      })

      if (error) {
        console.error('Failed to check CSV upload:', {
          error,
          userId: user.id,
          timestamp: new Date().toISOString(),
          errorCode: isStorageApiError(error) ? error.status : 'unknown'
        })
        throw new Error(
          `Failed to check CSV upload: ${error.message}. Error code: ${isStorageApiError(error) ? error.status : 'unknown'}. Please try refreshing the page.`
        )
      }

      // Find the user's file (filename should be user.id)
      const userFile = data?.find((file) => file.name === user.id)

      if (!userFile) {
        return null
      }

      // Get file metadata for original filename
      const { data: fileInfo, error: infoError } = await supabase.storage
        .from('user-csv-files')
        .info(user.id)

      if (infoError) {
        console.error('Failed to get file info:', {
          error: infoError,
          userId: user.id,
          fileName: user.id,
          timestamp: new Date().toISOString()
        })
        // Return basic info even if metadata fails
        return {
          fileName: 'workouts.csv',
          fileSize: userFile.metadata?.size || 0,
          uploadDate: userFile.updated_at || userFile.created_at
        }
      }

      return {
        fileName: fileInfo.metadata?.originalName || 'workouts.csv',
        fileSize: fileInfo.metadata?.size || 0,
        uploadDate: fileInfo.updatedAt || fileInfo.createdAt
      }
    },
    enabled: !!user
  })
}

export function useUploadCSV() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (file: File): Promise<void> => {
      if (!user) throw new Error('No authenticated user')

      // Validate file
      const fileName = file.name.toLowerCase()
      const validExtensions = ['.csv', '.xls', '.xlsx', '.xlsm', '.xlsb']
      const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext))
      const isExcelMimeType = file.type.includes('spreadsheet') || file.type.includes('excel') || file.type.includes('openxmlformats')
      const csvMimeTypes = [
        'text/csv',
        'text/comma-separated-values',
        'text/x-csv',
        'application/csv',
        'application/x-csv',
        'text/plain' // Sometimes used for CSV files
      ]
      const isCSVMimeType = file.type.includes('csv') || 
                           file.type.includes('comma-separated') || 
                           csvMimeTypes.includes(file.type.toLowerCase())
      
      if (!hasValidExtension && !isCSVMimeType && !isExcelMimeType) {
        throw new Error('Please upload a CSV or Excel file (.csv, .xls, .xlsx, .xlsm, .xlsb)')
      }

      if (file.size > 10 * 1024 * 1024) {
        // 10MB limit
        throw new Error('File size must be less than 10MB')
      }

      // Upload file to storage with user ID as filename
      // This will overwrite any existing file due to upsert: true
      const { error: uploadError } = await supabase.storage
        .from('user-csv-files')
        .upload(user.id, file, {
          cacheControl: '3600',
          upsert: true, // Allow overwriting existing file
          metadata: {
            originalName: file.name // Store original filename in metadata
          }
        })

      if (uploadError) {
        const errorContext = {
          error: uploadError,
          userId: user.id,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          timestamp: new Date().toISOString(),
          errorCode: isStorageApiError(uploadError) ? uploadError.status : 'unknown'
        }
        console.error('Failed to upload CSV:', errorContext)

        // Provide specific error messages based on error type
        if (uploadError.message.includes('payload too large')) {
          throw new Error(
            `File too large for upload: ${(file.size / 1024 / 1024).toFixed(2)} MB. Maximum allowed size may be smaller than 10MB. Try a smaller file.`
          )
        }
        if (uploadError.message.includes('permission')) {
          throw new Error(
            `Permission denied: Unable to upload file. Please try logging out and back in.`
          )
        }
        if (uploadError.message.includes('network')) {
          throw new Error(
            `Network error: Upload failed due to connection issues. Please check your internet connection and try again.`
          )
        }

        throw new Error(
          `Upload failed: ${uploadError.message}. Error code: ${isStorageApiError(uploadError) ? uploadError.status : 'unknown'}. Please try again or contact support.`
        )
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.userCSVUpload(user?.id || '') })
      queryClient.invalidateQueries({ queryKey: queryKeys.csvWorkoutData(user?.id || '', '') })
      queryClient.invalidateQueries({ queryKey: queryKeys.workouts.all })
      toast.success('CSV file uploaded successfully')
    },
    onError: (error) => {
      console.error('CSV upload failed:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to upload CSV')
    }
  })
}

export function useDeleteCSV() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (): Promise<void> => {
      if (!user) throw new Error('No authenticated user')

      // Delete from storage (filename is just user ID)
      const { error: storageError } = await supabase.storage
        .from('user-csv-files')
        .remove([user.id])

      if (storageError) {
        console.error('Failed to delete from storage:', {
          error: storageError,
          userId: user.id,
          timestamp: new Date().toISOString(),
          errorCode: isStorageApiError(storageError) ? storageError.status : 'unknown'
        })
        throw new Error(
          `Failed to delete file: ${storageError.message}. Error code: ${isStorageApiError(storageError) ? storageError.status : 'unknown'}. Please try again.`
        )
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.userCSVUpload(user?.id || '') })
      queryClient.invalidateQueries({ queryKey: queryKeys.csvWorkoutData(user?.id || '', '') })
      queryClient.invalidateQueries({ queryKey: queryKeys.workouts.all })
      toast.success('CSV file deleted')
    },
    onError: (error) => {
      console.error('CSV deletion failed:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to delete CSV')
    }
  })
}

export function useDownloadCSV() {
  const { user } = useAuth()

  return useMutation({
    mutationFn: async (): Promise<Blob> => {
      if (!user) throw new Error('No authenticated user')

      // Download from storage (filename is just user ID)
      const { data, error } = await supabase.storage.from('user-csv-files').download(user.id)

      if (error || !data) {
        console.error('Failed to download CSV:', {
          error,
          userId: user.id,
          timestamp: new Date().toISOString(),
          errorCode: error && isStorageApiError(error) ? error.status : 'unknown'
        })

        if (error?.message.includes('not found')) {
          throw new Error(
            'File not found: Your CSV file may have been deleted or expired. Please re-upload your CSV file.'
          )
        }
        if (error?.message.includes('permission')) {
          throw new Error(
            'Permission denied: Unable to download file. Please try logging out and back in.'
          )
        }

        throw new Error(
          `Download failed: ${error?.message || 'Unknown error'}. Error code: ${error && isStorageApiError(error) ? error.status : 'unknown'}. Please try again.`
        )
      }

      return data
    },
    onError: (error) => {
      console.error('CSV download failed:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to download CSV')
    }
  })
}
